import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { 
  ChevronDown, 
  ChevronRight, 
  Check, 
  X, 
  AlertTriangle, 
  Zap, 
  Sparkles,
  RotateCcw,
  Eye,
  EyeOff,
  Target,
  BarChart3,
  Edit2,
  Layers,
  Plus,
  Save
} from 'lucide-react';

// Risk badge component
function RiskBadge({ risk }) {
  const colors = {
    low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
  };
  
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[risk] || colors.medium}`}>
      {risk === 'high' && <AlertTriangle className="w-3 h-3 inline mr-1" />}
      {risk} risk
    </span>
  );
}

// Mode toggle component
function ModeToggle({ mode, onModeChange }) {
  return (
    <div className="flex items-center gap-2 p-1 bg-muted/50 rounded-full">
      <button
        onClick={() => onModeChange('subtle')}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
          mode === 'subtle' 
            ? 'bg-white dark:bg-slate-800 shadow-sm text-primary' 
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <Sparkles className="w-4 h-4" />
        Subtle
      </button>
      <button
        onClick={() => onModeChange('impact')}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
          mode === 'impact' 
            ? 'bg-white dark:bg-slate-800 shadow-sm text-primary' 
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <Zap className="w-4 h-4" />
        Impact
      </button>
    </div>
  );
}

// Editable change row component
function EditableChangeRow({ change, index, isEditing, editValue, onStartEdit, onSaveEdit, onCancelEdit, onEditChange }) {
  return (
    <div className="grid grid-cols-2 gap-3 text-sm">
      <div className="p-3 bg-red-50 dark:bg-red-900/10 rounded-lg">
        <p className="text-xs font-medium text-red-600 mb-1 flex items-center gap-1">
          <X className="w-3 h-3" /> Original ({change.element})
        </p>
        <p className="text-foreground whitespace-pre-wrap">{change.original}</p>
      </div>
      <div className="p-3 bg-green-50 dark:bg-green-900/10 rounded-lg relative group">
        <p className="text-xs font-medium text-green-600 mb-1 flex items-center gap-1">
          <Check className="w-3 h-3" /> Proposed
          {!isEditing && (
            <button 
              onClick={() => onStartEdit(index, change.proposed)}
              className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-green-100 rounded"
              title="Edit proposed text"
            >
              <Edit2 className="w-3 h-3" />
            </button>
          )}
        </p>
        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              value={editValue}
              onChange={(e) => onEditChange(e.target.value)}
              className="min-h-[80px] text-sm bg-white dark:bg-slate-900"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={onCancelEdit} className="h-7 px-2">
                <X className="w-3 h-3 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={() => onSaveEdit(index)} className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white">
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-foreground whitespace-pre-wrap">{change.proposed}</p>
        )}
      </div>
    </div>
  );
}

// Unchanged element row - for elements not being modified
function UnchangedElementRow({ element, onAddChange }) {
  const [isAdding, setIsAdding] = useState(false);
  const [proposedText, setProposedText] = useState('');
  
  const handleAdd = () => {
    if (proposedText.trim()) {
      onAddChange({
        element: element.type || 'text',
        original: element.content,
        proposed: proposedText.trim()
      });
      setIsAdding(false);
      setProposedText('');
    }
  };
  
  return (
    <div className="p-3 bg-muted/20 rounded-lg border border-border/50">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
            <span className="px-1.5 py-0.5 bg-muted rounded text-[10px] uppercase">{element.type || 'text'}</span>
            <span className="text-muted-foreground/60">— No changes proposed</span>
          </p>
          <p className="text-sm text-foreground/80 line-clamp-2">{element.content}</p>
        </div>
        {!isAdding && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsAdding(true)}
            className="shrink-0 h-7 px-2 text-muted-foreground hover:text-primary"
          >
            <Plus className="w-3 h-3 mr-1" /> Add Change
          </Button>
        )}
      </div>
      
      {isAdding && (
        <div className="mt-3 space-y-2 pt-3 border-t border-border/50">
          <p className="text-xs font-medium text-green-600">Proposed change:</p>
          <Textarea
            value={proposedText}
            onChange={(e) => setProposedText(e.target.value)}
            placeholder="Enter your proposed text..."
            className="min-h-[80px] text-sm"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setIsAdding(false); setProposedText(''); }} className="h-7 px-2">
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={handleAdd} disabled={!proposedText.trim()} className="h-7 px-2 bg-primary hover:bg-primary/90 text-white">
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Single recommended section card - ALWAYS EXPANDED by default
function RecommendedSectionCard({ section, mode, onApply, onRevert, onUpdateChanges, scrapedContent }) {
  const variant = mode === 'subtle' ? section.subtle_variant : section.impact_variant;
  const isApplied = section.applied_variant === mode;
  const hasOtherApplied = section.applied_variant && section.applied_variant !== mode;
  
  // State for editing proposed changes
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [localChanges, setLocalChanges] = useState(null);
  const [showUnchanged, setShowUnchanged] = useState(false);
  
  if (!variant) return null;
  
  // Use local changes if modified, otherwise use variant changes
  const changes = localChanges || variant.changes || [];
  const metricsToWatch = section.metrics_to_watch || [];
  
  // Get unchanged elements from scraped content or current_content_preview
  const getUnchangedElements = () => {
    const elements = [];
    
    // First try to get from scraped content array
    if (scrapedContent && Array.isArray(scrapedContent) && scrapedContent.length > 0) {
      const sectionType = section.section_type?.toLowerCase() || '';
      const sectionName = section.section_name?.toLowerCase() || '';
      
      // Filter scraped content to find matching sections
      const matchingSections = scrapedContent.filter(s => {
        const type = (s.section_type || '').toLowerCase();
        const content = (s.content || '').toLowerCase();
        
        if (type === sectionType) return true;
        if (sectionType === 'hero' && type === 'heading') return true;
        if (sectionType === 'features' && (type === 'body' || type === 'feature')) return true;
        if (sectionName && content.includes(sectionName.split(' ')[0])) return true;
        
        return false;
      });
      
      matchingSections.forEach(scraped => {
        if (scraped.content) {
          const type = scraped.section_type || 'text';
          const content = scraped.content.trim();
          if (content.length > 200) {
            const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 10);
            sentences.slice(0, 5).forEach(sentence => {
              elements.push({ type, content: sentence.trim() + '.' });
            });
          } else if (content) {
            elements.push({ type, content });
          }
        }
      });
    }
    
    // Fallback: parse from current_content_preview if no scraped content
    if (elements.length === 0 && section.current_content_preview) {
      const preview = section.current_content_preview.trim();
      if (preview.length > 50) {
        // Split by newlines or sentences
        const parts = preview.split(/[\n.!?]+/).filter(p => p.trim().length > 15);
        parts.slice(0, 5).forEach(part => {
          elements.push({ type: 'text', content: part.trim() });
        });
      } else if (preview) {
        elements.push({ type: 'text', content: preview });
      }
    }
    
    // Filter out elements that already have changes
    const changedOriginals = new Set(
      changes.map(c => c.original?.toLowerCase().trim().slice(0, 50))
    );
    
    return elements.filter(el => {
      const elContent = el.content?.toLowerCase().trim().slice(0, 50);
      return !changedOriginals.has(elContent);
    }).slice(0, 10);
  };
  
  const unchangedElements = getUnchangedElements();
  
  // Handle edit start
  const handleStartEdit = (index, currentValue) => {
    setEditingIndex(index);
    setEditValue(currentValue);
  };
  
  // Handle save edit
  const handleSaveEdit = (index) => {
    const newChanges = [...changes];
    newChanges[index] = { ...newChanges[index], proposed: editValue };
    setLocalChanges(newChanges);
    setEditingIndex(null);
    setEditValue('');
    
    // Notify parent of changes
    if (onUpdateChanges) {
      onUpdateChanges(section.section_id, mode, newChanges);
    }
  };
  
  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
  };
  
  // Handle adding a new change from unchanged element
  const handleAddChange = (newChange) => {
    const newChanges = [...changes, newChange];
    setLocalChanges(newChanges);
    
    // Notify parent of changes
    if (onUpdateChanges) {
      onUpdateChanges(section.section_id, mode, newChanges);
    }
  };
  
  return (
    <div 
      className={`rounded-xl border-2 overflow-hidden transition-all ${
        isApplied ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 
        hasOtherApplied ? 'border-amber-500/50 bg-amber-50/50 dark:bg-amber-900/10' :
        'border-border'
      }`}
      data-testid={`section-card-${section.section_id}`}
    >
      {/* Header */}
      <div className="p-4 bg-surface/50 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground">{section.section_name}</span>
                {section.above_the_fold && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    Above Fold
                  </span>
                )}
                <RiskBadge risk={section.risk_score} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {changes.length} change{changes.length !== 1 ? 's' : ''} • {variant.word_change_percent}% word change
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isApplied ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRevert(section.section_id)}
                className="rounded-full text-amber-600 border-amber-300 hover:bg-amber-50"
                data-testid={`revert-btn-${section.section_id}`}
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Revert
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => onApply(section.section_id, mode)}
                className={`rounded-full ${hasOtherApplied ? 'bg-amber-500 hover:bg-amber-600' : 'bg-primary hover:bg-primary/90'} text-white`}
                data-testid={`apply-btn-${section.section_id}`}
              >
                <Check className="w-3 h-3 mr-1" />
                Apply {mode === 'subtle' ? 'Subtle' : 'Impact'}
              </Button>
            )}
          </div>
        </div>
        
        {hasOtherApplied && (
          <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {section.applied_variant} variant currently applied
          </div>
        )}
      </div>
      
      {/* Content - ALWAYS VISIBLE */}
      <div className="p-4 space-y-4">
        {/* Strategy rationale */}
        {section.strategy_rationale && (
          <div className="bg-muted/30 rounded-lg p-3">
            <p className="text-xs font-semibold text-muted-foreground mb-1">STRATEGY</p>
            <p className="text-sm text-foreground">{section.strategy_rationale}</p>
          </div>
        )}
        
        {/* Changes - Editable */}
        <div className="space-y-3">
          <p className="text-xs font-semibold text-muted-foreground">PROPOSED CHANGES</p>
          {changes.map((change, idx) => (
            <EditableChangeRow
              key={idx}
              change={change}
              index={idx}
              isEditing={editingIndex === idx}
              editValue={editValue}
              onStartEdit={handleStartEdit}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={handleCancelEdit}
              onEditChange={setEditValue}
            />
          ))}
        </div>
        
        {/* Unchanged Elements - Collapsible */}
        {unchangedElements.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-border/50">
            <button
              onClick={() => setShowUnchanged(!showUnchanged)}
              className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
            >
              {showUnchanged ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              <span className="font-medium">Other elements in this section ({unchangedElements.length})</span>
              <span className="text-muted-foreground/60 ml-1">— click to add changes</span>
            </button>
            
            {showUnchanged && (
              <div className="space-y-2 mt-2">
                {unchangedElements.map((element, idx) => (
                  <UnchangedElementRow
                    key={idx}
                    element={element}
                    onAddChange={handleAddChange}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Hypothesis and metrics */}
        {(section.hypothesis || metricsToWatch.length > 0) && (
          <div className="grid grid-cols-2 gap-3">
            {section.hypothesis && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                  <Target className="w-3 h-3" /> HYPOTHESIS
                </p>
                <p className="text-sm text-foreground">{section.hypothesis}</p>
              </div>
            )}
            {metricsToWatch.length > 0 && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                  <BarChart3 className="w-3 h-3" /> METRICS TO WATCH
                </p>
                <div className="flex flex-wrap gap-1">
                  {metricsToWatch.map((metric, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded bg-muted text-foreground">
                      {metric.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Expandable section row for "all sections" - collapsed by default, expandable with edit
function AllSectionRow({ section, isExpanded, onToggleExpand, scrapedContent }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  
  const content = scrapedContent || section.current_content_preview || section.no_change_reason || 'No content available';
  
  const handleStartEdit = () => {
    setEditedContent(content);
    setIsEditing(true);
  };
  
  const handleSaveEdit = () => {
    // TODO: Save the edited content
    setIsEditing(false);
  };
  
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedContent('');
  };
  
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      {/* Header - clickable to expand */}
      <button
        onClick={() => onToggleExpand(section.section_id)}
        className="w-full flex items-center justify-between p-3 bg-muted/20 hover:bg-muted/30 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <span className="text-sm font-medium text-foreground">{section.section_name}</span>
          {section.above_the_fold && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              Above Fold
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {section.recommended ? 'Has recommendations' : 'No changes needed'}
        </span>
      </button>
      
      {/* Expanded content */}
      {isExpanded && (
        <div className="p-4 border-t border-border bg-white dark:bg-slate-900">
          {isEditing ? (
            <div className="space-y-3">
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="min-h-[120px] text-sm"
                placeholder="Edit section content..."
              />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                  <X className="w-3 h-3 mr-1" /> Cancel
                </Button>
                <Button size="sm" onClick={handleSaveEdit} className="bg-primary text-white">
                  <Check className="w-3 h-3 mr-1" /> Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">CURRENT CONTENT</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{content}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleStartEdit}
                  className="ml-4 text-muted-foreground hover:text-foreground"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
              {section.no_change_reason && (
                <div className="mt-3 p-2 bg-muted/30 rounded text-xs text-muted-foreground">
                  <span className="font-medium">Why no changes:</span> {section.no_change_reason}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Main component
export default function MessagingStep({ 
  sectionStrategy, 
  scrapedSections,
  onApplyVariant, 
  onRevertSection,
  onUpdateChanges,
  onConfirmMessaging,
  isLoading 
}) {
  const [mode, setMode] = useState('subtle');
  const [showAllSections, setShowAllSections] = useState(false);
  const [expandedAllSections, setExpandedAllSections] = useState(new Set());
  
  if (!sectionStrategy) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No section strategy generated yet.</p>
      </div>
    );
  }
  
  const recommendedSections = sectionStrategy.recommended_sections || [];
  const allSections = sectionStrategy.all_sections || [];
  
  // Get scraped content map for lookup
  const scrapedContentMap = {};
  if (scrapedSections) {
    scrapedSections.forEach(s => {
      scrapedContentMap[s.section_id || s.id] = s.content || s.body_copy || '';
    });
  }
  
  const toggleExpandAllSection = (sectionId) => {
    setExpandedAllSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };
  
  // Budget tracking - handle both flat and nested formats
  const rawBudget = sectionStrategy.change_budget || { subtle: 3, impact: 6 };
  // If budget[mode] is an object with 'total', extract it, otherwise use as-is
  const getBudgetValue = (b, m) => {
    const val = b[m];
    if (val && typeof val === 'object' && 'total' in val) {
      return val.total;
    }
    return typeof val === 'number' ? val : 3;
  };
  const budget = { subtle: getBudgetValue(rawBudget, 'subtle'), impact: getBudgetValue(rawBudget, 'impact') };
  const appliedCount = recommendedSections.filter(s => s.applied_variant).length;
  
  return (
    <div className="space-y-6" data-testid="messaging-step-content">
      {/* Header with mode toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Section-by-Section Changes</h3>
          <p className="text-sm text-muted-foreground">
            {recommendedSections.length} sections with recommendations • {appliedCount} applied
          </p>
        </div>
        <ModeToggle mode={mode} onModeChange={setMode} />
      </div>
      
      {/* Strategy summary */}
      {sectionStrategy.strategy_summary && (
        <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl p-4 border border-primary/20">
          <p className="text-sm text-foreground">{sectionStrategy.strategy_summary}</p>
        </div>
      )}
      
      {/* Budget indicator */}
      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-muted-foreground">
            Change Budget ({mode}):
          </span>
          <span className={`font-medium ${appliedCount > budget[mode] ? 'text-red-600' : 'text-foreground'}`}>
            {appliedCount} / {budget[mode]} sections
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          Baseline preserved: ~{sectionStrategy.baseline_preservation?.[mode] || 80}%
        </div>
      </div>
      
      {/* Recommended sections - ALWAYS EXPANDED */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-foreground flex items-center gap-2">
            <Target className="w-4 h-4 text-primary" />
            Recommended Sections
          </h4>
          <span className="text-xs text-muted-foreground">
            {recommendedSections.length} section{recommendedSections.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        <div className="space-y-4">
          {recommendedSections.map((section) => (
            <RecommendedSectionCard
              key={section.section_id}
              section={section}
              mode={mode}
              onApply={onApplyVariant}
              onRevert={onRevertSection}
              onUpdateChanges={onUpdateChanges}
              scrapedContent={scrapedSections}
            />
          ))}
        </div>
        
        {recommendedSections.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p>No section changes recommended for this campaign.</p>
          </div>
        )}
      </div>
      
      {/* All website sections - collapsed by default, can expand and edit */}
      {allSections.length > 0 && (
        <div className="space-y-3 pt-4 border-t border-border">
          <button
            onClick={() => setShowAllSections(!showAllSections)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showAllSections ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <Layers className="w-4 h-4" />
            {showAllSections ? 'Hide' : 'Show'} all website sections ({allSections.length})
          </button>
          
          {showAllSections && (
            <div className="space-y-2 mt-3">
              <p className="text-xs text-muted-foreground mb-2">
                All sections from your website. Click to expand and edit any section.
              </p>
              {allSections.map((section) => (
                <AllSectionRow 
                  key={section.section_id} 
                  section={section}
                  isExpanded={expandedAllSections.has(section.section_id)}
                  onToggleExpand={toggleExpandAllSection}
                  scrapedContent={scrapedContentMap[section.section_id]}
                />
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Confirm button */}
      <Button
        onClick={onConfirmMessaging}
        disabled={isLoading || appliedCount === 0}
        className="w-full bg-primary text-white hover:bg-primary/90 rounded-full py-6"
        data-testid="confirm-messaging-btn"
      >
        {isLoading ? (
          <>Processing...</>
        ) : (
          <>
            Confirm {appliedCount} Section Change{appliedCount !== 1 ? 's' : ''} & Continue
          </>
        )}
      </Button>
    </div>
  );
}
